
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField, FileUpload } from "./BaseForm";
import { FleetInsuranceFormData, FormProps } from "@/types/forms";

type FleetInsuranceFormProps = FormProps<FleetInsuranceFormData>;

const FleetInsuranceForm = ({ onSubmit, initialData = {}, buttonText = "Continue" }: FleetInsuranceFormProps) => {
  const [formData, setFormData] = useState<FleetInsuranceFormData>({
    numberOfVehicles: initialData.numberOfVehicles || "",
    fleetFile: initialData.fleetFile || null,
    claimsLast3Years: initialData.claimsLast3Years || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.numberOfVehicles) newErrors.numberOfVehicles = "Number of vehicles is required";
    if (!formData.fleetFile) newErrors.fleetFile = "Fleet file upload is required";
    if (!formData.claimsLast3Years) newErrors.claimsLast3Years = "Claims information is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = <K extends keyof FleetInsuranceFormData>(field: K, value: FleetInsuranceFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormField label="Number of vehicles in the fleet" required>
        <Input
          type="number"
          placeholder="Enter number of vehicles"
          value={formData.numberOfVehicles}
          onChange={(e) => updateField('numberOfVehicles', e.target.value)}
        />
        {errors.numberOfVehicles && <p className="text-red-500 text-sm">{errors.numberOfVehicles}</p>}
      </FormField>

      <FileUpload
        label="Upload Fleet File (Excel: value, make, model, trim, YOM, YOR)"
        accept=".xls,.xlsx"
        required
        value={formData.fleetFile}
        onChange={(file) => updateField('fleetFile', file)}
      />
      {errors.fleetFile && <p className="text-red-500 text-sm">{errors.fleetFile}</p>}

      <FormField label="How many claims have you had in the past 3 years?" required>
        <Input
          type="number"
          placeholder="Enter number of claims"
          value={formData.claimsLast3Years}
          onChange={(e) => updateField('claimsLast3Years', e.target.value)}
        />
        {errors.claimsLast3Years && <p className="text-red-500 text-sm">{errors.claimsLast3Years}</p>}
      </FormField>

      <Button type="submit" className="w-full">
        {buttonText}
      </Button>
    </form>
  );
};

export default FleetInsuranceForm;
